/**
 * Markdown Formatter for Code Review Results
 */

import type { CodeReviewResult, CodeReviewFinding, ReviewSeverity } from '../types.js';

const SEVERITY_EMOJI: Record<ReviewSeverity, string> = {
  blocking: '🔴',
  critical: '🟠',
  warning: '🟡',
  info: '🔵',
};

const GRADE_EMOJI: Record<string, string> = {
  A: '🟢',
  B: '🟢',
  C: '🟡',
  D: '🟠',
  F: '🔴',
};

export function formatMarkdownReport(result: CodeReviewResult): string {
  const lines: string[] = [];

  // Header
  lines.push('# Code Review Report');
  lines.push('');

  // Summary
  lines.push('## Summary');
  lines.push('');
  lines.push('| Metric | Value |');
  lines.push('|--------|-------|');
  lines.push(`| Status | ${result.summary.status === 'passed' ? '✅ PASSED' : result.summary.status === 'failed' ? '❌ FAILED' : '⚠️ WARNING'} |`);
  lines.push(`| Score | ${result.summary.score}/100 |`);
  lines.push(`| Grade | ${GRADE_EMOJI[result.summary.grade]} ${result.summary.grade} |`);
  lines.push(`| Files Scanned | ${result.stats.filesScanned} |`);
  lines.push(`| Lines Scanned | ${result.stats.linesScanned.toLocaleString()} |`);
  lines.push(`| Duration | ${result.stats.duration}ms |`);
  lines.push('');

  // Issues Count
  lines.push('### Issues Found');
  lines.push('');
  lines.push('| Severity | Count |');
  lines.push('|----------|-------|');
  lines.push(`| ${SEVERITY_EMOJI.blocking} Blocking | ${result.stats.findingsCount.blocking} |`);
  lines.push(`| ${SEVERITY_EMOJI.critical} Critical | ${result.stats.findingsCount.critical} |`);
  lines.push(`| ${SEVERITY_EMOJI.warning} Warning | ${result.stats.findingsCount.warning} |`);
  lines.push(`| ${SEVERITY_EMOJI.info} Info | ${result.stats.findingsCount.info} |`);
  lines.push('');

  // By Category
  const categoriesWithIssues = Object.entries(result.stats.byCategory)
    .filter(([_, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  if (categoriesWithIssues.length > 0) {
    lines.push('### By Category');
    lines.push('');
    lines.push('| Category | Count |');
    lines.push('|----------|-------|');
    for (const [category, count] of categoriesWithIssues) {
      lines.push(`| ${formatCategoryName(category)} | ${count} |`);
    }
    lines.push('');
  }

  // Findings by Severity
  const blockingFindings = result.findings.filter((f) => f.severity === 'blocking');
  const criticalFindings = result.findings.filter((f) => f.severity === 'critical');
  const warningFindings = result.findings.filter((f) => f.severity === 'warning');
  const infoFindings = result.findings.filter((f) => f.severity === 'info');

  if (blockingFindings.length > 0) {
    lines.push('---');
    lines.push('');
    lines.push(`## ${SEVERITY_EMOJI.blocking} Blocking Issues (${blockingFindings.length})`);
    lines.push('');
    lines.push('> These issues MUST be fixed before merging.');
    lines.push('');
    formatFindings(lines, blockingFindings);
  }

  if (criticalFindings.length > 0) {
    lines.push('---');
    lines.push('');
    lines.push(`## ${SEVERITY_EMOJI.critical} Critical Issues (${criticalFindings.length})`);
    lines.push('');
    lines.push('> These issues should be fixed as soon as possible.');
    lines.push('');
    formatFindings(lines, criticalFindings);
  }

  if (warningFindings.length > 0) {
    lines.push('---');
    lines.push('');
    lines.push(`## ${SEVERITY_EMOJI.warning} Warnings (${warningFindings.length})`);
    lines.push('');
    formatFindings(lines, warningFindings);
  }

  if (infoFindings.length > 0) {
    lines.push('---');
    lines.push('');
    lines.push(`## ${SEVERITY_EMOJI.info} Info (${infoFindings.length})`);
    lines.push('');
    formatFindings(lines, infoFindings);
  }

  // No issues
  if (result.findings.length === 0) {
    lines.push('---');
    lines.push('');
    lines.push('## ✨ No Issues Found');
    lines.push('');
    lines.push('Great job! Your code passed all checks.');
    lines.push('');
  }

  return lines.join('\n');
}

function formatFindings(lines: string[], findings: CodeReviewFinding[]): void {
  for (const finding of findings) {
    lines.push(`### ${finding.id}: ${finding.title}`);
    lines.push('');
    lines.push(`**Category**: ${formatCategoryName(finding.category)}`);
    lines.push(`**File**: \`${finding.file}${finding.line ? `:${finding.line}` : ''}\``);
    if (finding.cweId) {
      lines.push(`**CWE**: ${finding.cweId}`);
    }
    lines.push('');
    lines.push(finding.description);
    lines.push('');
    if (finding.code) {
      lines.push('**Code**:');
      lines.push('```');
      lines.push(finding.code);
      lines.push('```');
      lines.push('');
    }
    lines.push(`**Fix**: ${finding.suggestion}`);
    if (finding.autoFixable) {
      lines.push('');
      lines.push('> 💡 This issue can be auto-fixed.');
    }
    if (finding.references && finding.references.length > 0) {
      lines.push('');
      lines.push('**References**:');
      for (const ref of finding.references) {
        lines.push(`- ${ref}`);
      }
    }
    lines.push('');
  }
}

function formatCategoryName(category: string): string {
  const names: Record<string, string> = {
    security: 'Security',
    architecture: 'Architecture',
    'hardcoded-values': 'Hardcoded Values',
    tests: 'Tests',
    'ai-hallucinations': 'AI Hallucinations',
    performance: 'Performance',
    'dead-code': 'Dead Code',
    i18n: 'Internationalization',
    accessibility: 'Accessibility',
  };
  return names[category] || category;
}
